import React from "react"
import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import Select from "react-select"
import moment from "moment"
import "moment-timezone"
import PhoneInput from "react-phone-number-input"
import "react-phone-number-input/style.css"

import { nationalities, countries, genderOptions, seatOptions, mealOptions } from "../../lib/constants"
import { useData } from "../../contexts/DataContext"
import Loader from "../../components/Loader/index.js"

import { BookingFormSchema } from "./schema.js"
import styles from "./index.module.css"


const BookingForm = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { inventory, airports, flights, loading: dataLoading } = useData()
    const [loading, setLoading] = useState(false)
    const [flightDetails, setFlightDetails] = useState([])

    // Check if we have existing booking data from location.state (when coming back from summary)
    const stateBookingData = location.state?.bookingData

    const formData = {
        promoCode: stateBookingData?.promoCode,
        outboundFlight: stateBookingData?.outboundFlight,
        inboundFlight: stateBookingData?.inboundFlight,
        passengers: stateBookingData?.passengers?.length || stateBookingData?.passengerCount || 0,
    }

    const preprocessPassenger = (p) => ({
        ...p,
        dob: p.dob ? moment(p.dob).format("YYYY-MM-DD") : "",
        passportExpiration: p.passportExpiration ? moment(p.passportExpiration).format("YYYY-MM-DD") : "",
    })

    const defaultPassengers = stateBookingData?.passengers
        ? stateBookingData.passengers.map(preprocessPassenger)
        : Array.from({ length: formData.passengers }, () => ({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            dob: "",
            passportExpiration: "",
            gender: "",
            nationality: "",
            passportCountry: "",
            passportNumber: "",
            seatType: "",
            meal: "",
        }))

    // Initialize react-hook-form with Yup resolver
    const { register, handleSubmit, control, formState: { errors } } = useForm({
        resolver: yupResolver(BookingFormSchema),
        defaultValues: {
            outboundFlight: formData.outboundFlight,
            inboundFlight: formData.inboundFlight,
            promoCode: formData.promoCode,
            passengers: defaultPassengers,
        },
    })

    const onSubmit = async (data) => {
        try {
            setLoading(true)
            const totalCost = flightDetails.reduce((acc, flight) => acc + parseFloat(flight.price), 0) * data.passengers.length
            const bookingData = JSON.parse(JSON.stringify({
                ...data,
                totalCost: totalCost,
            }))

            let departureFlight = flightDetails[0]
            let returnFlight = flightDetails.length > 1 ? flightDetails[1] : null

            let dataToPass = {
                bookingData: bookingData,
                departureFlight: { ...departureFlight, departureDateTime: undefined, arrivalDateTime: undefined },
                returnFlight: { ...returnFlight, departureDateTime: undefined, arrivalDateTime: undefined },
            }

            navigate(`/summary`, {
                state: dataToPass
            })

        } catch (error) {
            console.error("Error submitting booking:", error)
            setLoading(false)
            alert("Unable to process your booking. Please check your information and try again.")
        }
    }

    useEffect(() => {
        if (dataLoading) return

        const processFlights = (inventory) => {
            return inventory.map((ticket) => {
                let departure_timezone = airports.find((item) => item.code === ticket.departure)?.timezone
                let arrival_timezone = airports.find((item) => item.code === ticket.arrival)?.timezone

                let departureDateTime = moment(`${ticket.departureDate} ${ticket.departureTime}`, "YYYY-MM-DD HH:mm").tz(departure_timezone, true)
                let arrivalDateTime = moment(`${ticket.departureDate} ${ticket.arrivalTime.split("(+1)")[0]}`, "YYYY-MM-DD HH:mm")
                let airlineLogo = flights.find(flight => flight.flightNo === ticket.flightNo)?.airlineLogo


                arrivalDateTime = ticket.arrivalTime.includes("(+1)")
                    ? arrivalDateTime.tz(arrival_timezone, true).add(1, "day")
                    : arrivalDateTime.tz(arrival_timezone, true)

                let duration = moment.duration(arrivalDateTime.diff(departureDateTime))
                return {
                    ...ticket,
                    departureDateTime,
                    arrivalDateTime,
                    airlineLogo,
                    duration: `${duration.hours()} hrs ${duration.minutes()} mins`,
                }
            })
        }

        // Process outbound flight
        let outboundFlight = inventory.find(item => item.id === formData.outboundFlight)
        let processedFlights = outboundFlight ? processFlights([outboundFlight]) : []

        // Only process return flight if it exists and we have inboundFlight ID
        if (formData.inboundFlight) {
            let inboundFlight = inventory.find(item => item.id === formData.inboundFlight)
            if (inboundFlight) {
                processedFlights = [...processedFlights, ...processFlights([inboundFlight])]
            }
        }

        setFlightDetails(processedFlights)
    }, [formData.outboundFlight, formData.inboundFlight, inventory, airports, flights, dataLoading])

    return (
        <section className="py-4">
            <div className="row my-3">
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label className={`form-label ${styles.label}`}>
                            From
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                flight_takeoff
                            </span>
                        </label>
                        <input type="text" className="form-control" value={airports.find((item) => item.code === flightDetails[0]?.departure)?.label || ""} disabled />
                    </div>
                </div>
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label className={`form-label ${styles.label}`}>
                            To
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                flight_land
                            </span>
                        </label>
                        <input type="text" className="form-control" value={airports.find((item) => item.code === flightDetails[0]?.arrival)?.label || ""} disabled />
                    </div>
                </div>
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label htmlFor="departureDateInput" className={`form-label ${styles.label}`}>
                            Departure Date
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                calendar_month
                            </span>
                        </label>
                        <input type="text" className="form-control" value={flightDetails[0]?.departureDate} disabled />
                    </div>
                </div>
                {flightDetails.length > 1 &&
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label htmlFor="departureDateInput" className={`form-label ${styles.label}`}>
                                Return Date
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    calendar_month
                                </span>
                            </label>
                            <input type="text" className="form-control" value={flightDetails[1].departureDate} disabled />
                        </div>
                    </div>
                }
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label htmlFor="passengerCountInput" className={`form-label ${styles.label}`}>
                            No of Seats
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                airline_seat_recline_extra
                            </span>
                        </label>
                        <input type="text" className="form-control" value={formData.passengers} disabled />
                    </div>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-5">
                <h4 className="heading-text-primary">Flight Details</h4>
            </div>
            {dataLoading ? <Loader /> : <><div className="row my-3">
                {flightDetails && flightDetails.length > 0 ? flightDetails.map((flight) =>
                    <div className={`col`} key={`flight-${flight.id}`}>
                        <section className={`${styles.flightDetailWrapper}`}>
                            <div className={styles.flightDetailBody}>
                                <span className={styles.flightDepartureWrapper}>
                                    <p className={styles.flightDate}>
                                        {moment(flight.departureDateTime).format("ddd DD MMM")}
                                    </p>
                                    <p className={styles.flightTime}>
                                        {flight.departureTime}
                                    </p>
                                    <p className={styles.flightLocCode}>
                                        {flight.departure}
                                    </p>
                                    <p className={styles.flightLoc}>
                                        {airports.find((item) => item.code === flight.departure)?.label || ""}
                                    </p>
                                </span>
                                <span className={styles.flightDurationWrapper}>
                                    <span className={styles.duration}>{flight.duration}</span>
                                    <div className={styles.line}>
                                        <span className={styles.arrow}></span>
                                    </div>
                                    <img
                                        className="mt-2"
                                        src={flight.airlineLogo}
                                        alt={`${flight.flightNo} logo`}
                                        width={"100px"}
                                    />
                                </span>
                                <span className={styles.flightArrivalWrapper}>
                                    <p className={styles.flightDate}>
                                        {moment(flight.arrivalDateTime).format("ddd DD MMM")}
                                    </p>
                                    <p className={styles.flightTime}>
                                        {flight.arrivalTime.split("(+1)")[0]}
                                    </p>
                                    <p className={styles.flightLocCode}>
                                        {flight.arrival}
                                    </p>
                                    <p className={styles.flightLoc}>
                                        {airports.find((item) => item.code === flight.arrival)?.label || ""}
                                    </p>
                                </span>
                            </div>
                            <div className={styles.flightDetailFooter}>
                                <span className={styles.flightNo}>
                                    <span className={`material-symbols-outlined ${styles.flightNoIcon}`}>
                                        flight
                                    </span>
                                    <span className={styles.flightPlane}>A330</span>
                                    {flight.flightNo}
                                </span>
                                <span className={styles.flightPrice}>${flight.price}</span>
                            </div>
                        </section>
                    </div>) :
                    <div className="col">
                        <p>No Flights Available</p>
                    </div>
                }
            </div>
                <div className="d-flex justify-content-end">
                    {formData.returnDate && flightDetails.length > 0 && (
                        <h4 className="heading-text-primary">
                            Total: ${flightDetails.reduce((acc, flight) => acc + parseFloat(flight.price), 0).toFixed(2)}
                        </h4>
                    )}
                </div></>}
            <h4 className="heading-text-primary pb-2">Passenger Details</h4>
            <form className={styles.passengerForm} onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" {...register("outboundFlight")} value={formData.outboundFlight} />
                <input type="hidden" {...register("inboundFlight")} value={formData.inboundFlight} />
                <input type="hidden" {...register("promoCode")} value={formData.promoCode} />
                {Array.from({ length: formData.passengers }).map((_, index) => (
                    <section key={`passengerFormSection-${index}`} className={styles.passengerFormSection}>
                        <h4 className="heading-text-primary">
                            <span className="material-symbols-outlined">
                                airline_seat_recline_extra
                            </span> #{index + 1}
                        </h4>
                        <div className="row mb-3">
                            <div className={`form-group col`}>
                                <label htmlFor={`firstName-${index}`} className={`form-label ${styles.label}`}>First Name</label>
                                <input
                                    type="text"
                                    id={`firstName-${index}`}
                                    {...register(`passengers.${index}.firstName`)}
                                    className={`form-control ${errors.passengers?.[index]?.firstName ? "is-invalid" : ""}`}
                                    placeholder="First Name"
                                />
                                {errors.passengers?.[index]?.firstName && (
                                    <div className="invalid-feedback">{errors.passengers[index].firstName.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`lastName-${index}`} className={`form-label ${styles.label}`}>Last Name</label>
                                <input
                                    type="text"
                                    id={`lastName-${index}`}
                                    {...register(`passengers.${index}.lastName`)}
                                    className={`form-control ${errors.passengers?.[index]?.lastName ? "is-invalid" : ""}`}
                                    placeholder="Last Name"
                                />
                                {errors.passengers?.[index]?.lastName && (
                                    <div className="invalid-feedback">{errors.passengers[index].lastName.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`email-${index}`} className={`form-label ${styles.label}`}>Email</label>
                                <input
                                    type="email"
                                    id={`email-${index}`}
                                    {...register(`passengers.${index}.email`)}
                                    className={`form-control ${errors.passengers?.[index]?.email ? "is-invalid" : ""}`}
                                    placeholder="Email"
                                />
                                {errors.passengers?.[index]?.email && (
                                    <div className="invalid-feedback">{errors.passengers[index].email.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`phone-${index}`} className={`form-label ${styles.label}`}>Phone</label>
                                <Controller
                                    name={`passengers.${index}.phone`}
                                    control={control}
                                    render={({ field }) => (
                                        <PhoneInput
                                            {...field}
                                            defaultCountry="US"
                                            placeholder={"123 456 7890"}
                                            className={`form-control ${styles.phoneInput} ${errors.passengers?.[index]?.phone ? "is-invalid" : ""}`}
                                            onChange={(value) => field.onChange(value)}
                                            value={field.value}
                                        />
                                    )}
                                />
                                {errors.passengers?.[index]?.phone && (
                                    <div className="invalid-feedback">{errors.passengers[index].phone.message}</div>
                                )}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="form-group col">
                                <label htmlFor={`dob-${index}`} className={`form-label ${styles.label}`}>Date of Birth</label>
                                <input
                                    type="date"
                                    id={`dob-${index}`}
                                    {...register(`passengers.${index}.dob`)}
                                    className={`form-control ${errors.passengers?.[index]?.dob ? "is-invalid" : ""}`}
                                    max={moment().format('YYYY-MM-DD')}
                                />
                                {errors.passengers?.[index]?.dob && (
                                    <div className="invalid-feedback">{errors.passengers[index].dob.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`passportExpiration-${index}`} className={`form-label ${styles.label}`}>Passport Expiration</label>
                                <input
                                    type="date"
                                    id={`passportExpiration-${index}`}
                                    {...register(`passengers.${index}.passportExpiration`)}
                                    className={`form-control ${errors.passengers?.[index]?.passportExpiration ? "is-invalid" : ""}`}
                                    min={moment().format('YYYY-MM-DD')}
                                />
                                {errors.passengers?.[index]?.passportExpiration && (
                                    <div className="invalid-feedback">{errors.passengers[index].passportExpiration.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`gender-${index}`} className={`form-label ${styles.label}`}>Gender</label>
                                <Controller
                                    name={`passengers.${index}.gender`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={genderOptions}
                                            placeholder="Select Gender"
                                            className={`${errors.passengers?.[index]?.gender ? "is-invalid" : ""}`}
                                            value={genderOptions.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => { field.onChange(selectedOption ? selectedOption.value : "") }}
                                        />
                                    )}
                                />
                                {errors.passengers?.[index]?.gender && (
                                    <div className="invalid-feedback">{errors.passengers[index].gender.message}</div>
                                )}
                            </div>
                            <div className="form-group col">
                                <label htmlFor={`nationality-${index}`} className={`form-label ${styles.label}`}>Nationality</label>
                                <Controller
                                    name={`passengers.${index}.nationality`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={nationalities}
                                            placeholder="Select Nationality"
                                            className={`${errors.passengers?.[index]?.nationality ? "is-invalid" : ""}`}
                                            value={nationalities.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => { field.onChange(selectedOption ? selectedOption.value : "") }}
                                        />
                                    )}
                                />
                                {errors.passengers?.[index]?.nationality && (
                                    <div className="invalid-feedback">{errors.passengers[index].nationality.message}</div>
                                )}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="form-group col-3">
                                <label htmlFor={`passportCountry-${index}`} className={`form-label ${styles.label}`}>Passport Country</label>
                                <Controller
                                    name={`passengers.${index}.passportCountry`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={countries}
                                            placeholder="Select Passport Country"
                                            className={`${errors.passengers?.[index]?.passportCountry ? "is-invalid" : ""}`}
                                            value={countries.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => { field.onChange(selectedOption ? selectedOption.value : "") }}
                                        />
                                    )}
                                />
                                {errors.passengers?.[index]?.passportCountry && (
                                    <div className="invalid-feedback">{errors.passengers[index].passportCountry.message}</div>
                                )}
                            </div>
                            <div className="form-group col-3">
                                <label htmlFor={`passportNumber-${index}`} className={`form-label ${styles.label}`}>Passport Number</label>
                                <input
                                    type="text"
                                    id={`passportNumber-${index}`}
                                    {...register(`passengers.${index}.passportNumber`)}
                                    className={`form-control ${errors.passengers?.[index]?.passportNumber ? "is-invalid" : ""}`}
                                    placeholder="Passport Number"
                                />
                                {errors.passengers?.[index]?.passportNumber && (
                                    <div className="invalid-feedback">{errors.passengers[index].passportNumber.message}</div>
                                )}
                            </div>
                            <div className="form-group col-3">
                                <label htmlFor={`seatType-${index}`} className={`form-label ${styles.label}`}>Seat Type</label>
                                <Controller
                                    name={`passengers.${index}.seatType`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={seatOptions}
                                            placeholder="Select Seat Type"
                                            className={`${errors.passengers?.[index]?.seatType ? "is-invalid" : ""}`}
                                            value={seatOptions.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => { field.onChange(selectedOption ? selectedOption.value : "") }}
                                        />
                                    )}
                                />
                                {errors.passengers?.[index]?.seatType && (
                                    <div className="invalid-feedback">{errors.passengers[index].seatType.message}</div>
                                )}
                            </div>
                            <div className="form-group col-3">
                                <label htmlFor={`meal-${index}`} className={`form-label ${styles.label}`}>Meal</label>
                                <Controller
                                    name={`passengers.${index}.meal`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={mealOptions}
                                            placeholder="Select Special Meal"
                                            className={`${errors.passengers?.[index]?.meal ? "is-invalid" : ""}`}
                                            value={mealOptions.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => { field.onChange(selectedOption ? selectedOption.value : "") }}
                                        />
                                    )}
                                />
                                <small className="form-text text-muted">NOTE: All food is Kosher.</small>
                                {errors.passengers?.[index]?.meal && (
                                    <div className="invalid-feedback">{errors.passengers[index].meal.message}</div>
                                )}
                            </div>
                        </div>
                    </section>
                ))}

                <div className={styles.buttonContainer}>
                    <button type="submit" className="btn-primary" disabled={loading || dataLoading}>
                        Continue{loading && <>&nbsp;<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></>}
                    </button>
                </div>
            </form>
        </section>
    )
}

export default BookingForm